import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { FloatingPortal, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { AllSelection, TextSelection } from '@tiptap/pm/state';
import { useMediaQuery } from '@uidotdev/usehooks';
import { Bold, Code, Italic, MessageSquareQuote, Strikethrough } from 'lucide-react';

import { BREAKPOINT_SM } from '../lib/breakpoints';
import { cn } from '../lib/utils';

import { Tooltip } from './shared/Tooltip';

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');

// Renders a TipTap default keybinding for the tooltip, per platform: ⌘⇧S / Ctrl+Shift+S.
const shortcutLabel = (key, { shift = false } = {}) =>
  IS_MAC ? `⌘${shift ? '⇧' : ''}${key}` : `Ctrl+${shift ? 'Shift+' : ''}${key}`;

// Commits on mousedown with preventDefault so clicking never blurs the editor or
// collapses the selection the toolbar is operating on.
function SelectionToolbarButton({ icon: Icon, label, shortcut, isActive, onImage, onAction }) {
  const handleMouseDown = (e) => {
    e.preventDefault();
    onAction();
  };

  return (
    <Tooltip label={shortcut} placement="top" className="flex shrink-0">
      <button
        type="button"
        onMouseDown={handleMouseDown}
        aria-label={label}
        data-active={isActive ? 'true' : 'false'}
        className={cn(
          'flex size-7 cursor-pointer items-center justify-center rounded-full transition-none [&_*]:transition-none',
          onImage
            ? isActive
              ? 'bg-gray-200 text-black'
              : 'text-gray-100 hover:bg-white/10 hover:text-white'
            : isActive
              ? 'bg-black text-white dark:bg-white dark:text-black'
              : 'text-gray-500 hover:bg-black/5 hover:text-black dark:text-gray-100 dark:hover:bg-white/10 dark:hover:text-white',
        )}
      >
        <Icon className="size-4" strokeWidth={2} absoluteStrokeWidth />
      </button>
    </Tooltip>
  );
}

// Floating formatting helper over the composer's highlighted text. Unlike the old
// TipTap BubbleMenu (disabled because the composer's overflow clipped it), this renders
// through a FloatingPortal anchored to the selection rect, so nothing can clip it.
function ComposerSelectionToolbar({ editor }) {
  const isBreakpointSm = useMediaQuery(BREAKPOINT_SM);
  const [selectionState, setSelectionState] = useState(null);
  const pointerDownRef = useRef(false);

  useEffect(() => {
    // TipTap can briefly hand React an editor whose view was already destroyed.
    if (!editor || editor.isDestroyed) return undefined;

    const computeActives = () => ({
      isBold: editor.isActive('bold'),
      isItalic: editor.isActive('italic'),
      isStrike: editor.isActive('strike'),
      isCode: editor.isActive('code'),
      isBlockquote: editor.isActive('blockquote'),
    });

    const update = () => {
      if (editor.isDestroyed) {
        setSelectionState(null);
        return;
      }

      const { state, view } = editor;
      const { selection } = state;

      // Text selections and select-all (⌘A makes an AllSelection, not a TextSelection)
      // get the helper — node selections (entity chips) and collapsed carets don't.
      const isTextLikeSelection = selection instanceof TextSelection || selection instanceof AllSelection;
      if (selection.empty || !isTextLikeSelection || !view.hasFocus()) {
        setSelectionState(null);
        return;
      }

      setSelectionState((prev) => {
        // Once shown, the position is locked — later transactions (mark toggles,
        // quote/list wraps that reflow the content) only refresh the active states.
        // The bar moves again only after the selection is dropped and remade.
        if (prev) return { ...prev, ...computeActives() };

        // Mid-drag: stay hidden until mouseup settles the selection.
        if (pointerDownRef.current) return prev;

        // AllSelection's endpoints are block boundaries, where coordsAtPos returns a
        // rect spanning the block's full width — centering the bar on the composer
        // instead of the text. Clamp to the nearest text positions so ⌘A measures
        // the same caret coords a mouse selection would.
        const from = selection instanceof AllSelection ? TextSelection.atStart(state.doc).from : selection.from;
        const to = selection instanceof AllSelection ? TextSelection.atEnd(state.doc).to : selection.to;
        const start = view.coordsAtPos(from);
        const end = view.coordsAtPos(to, -1);
        return {
          rect: {
            top: Math.min(start.top, end.top),
            bottom: Math.max(start.bottom, end.bottom),
            left: Math.min(start.left, end.left),
            right: Math.max(start.right, end.right),
          },
          // Portaled to body, so the .has-workspace-background-image ancestor scoping
          // can't style this — read the mode off the editor's DOM instead.
          onImage: Boolean(view.dom.closest('.has-workspace-background-image')),
          ...computeActives(),
        };
      });
    };

    const hide = () => setSelectionState(null);
    const handleMouseDown = () => {
      pointerDownRef.current = true;
    };
    const handleMouseUp = () => {
      if (!pointerDownRef.current) return;
      pointerDownRef.current = false;
      // Let ProseMirror commit the final selection before evaluating it.
      requestAnimationFrame(update);
    };

    const dom = editor.view.dom;
    dom.addEventListener('mousedown', handleMouseDown);
    // On document: drags routinely end outside the editor.
    document.addEventListener('mouseup', handleMouseUp);
    // 'transaction' covers selection changes AND mark toggles (which keep the selection).
    editor.on('transaction', update);
    editor.on('blur', hide);
    editor.on('focus', update);

    return () => {
      dom.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      editor.off('transaction', update);
      editor.off('blur', hide);
      editor.off('focus', update);
    };
  }, [editor]);

  const rect = selectionState?.rect || null;
  const isOpen = Boolean(rect) && !isBreakpointSm;

  const virtualReference = useMemo(() => {
    if (!rect) return null;

    return {
      getBoundingClientRect() {
        return {
          x: rect.left,
          y: rect.top,
          width: rect.right - rect.left,
          height: rect.bottom - rect.top,
          top: rect.top,
          bottom: rect.bottom,
          left: rect.left,
          right: rect.right,
        };
      },
    };
  }, [rect]);

  const { refs, floatingStyles } = useFloating({
    open: isOpen,
    placement: 'top',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
    // Position via top/left so the entrance animation can own `transform`.
    transform: false,
  });

  useLayoutEffect(() => {
    refs.setPositionReference(virtualReference);
  }, [refs, virtualReference]);

  if (!isOpen || !editor) return null;

  const { onImage } = selectionState;

  const buttons = [
    {
      icon: Bold,
      label: 'Bold',
      shortcut: shortcutLabel('B'),
      isActive: selectionState.isBold,
      onAction: () => editor.chain().focus().toggleBold().run(),
    },
    {
      icon: Italic,
      label: 'Italic',
      shortcut: shortcutLabel('I'),
      isActive: selectionState.isItalic,
      onAction: () => editor.chain().focus().toggleItalic().run(),
    },
    {
      icon: Strikethrough,
      label: 'Strike',
      shortcut: shortcutLabel('S', { shift: true }),
      isActive: selectionState.isStrike,
      onAction: () => editor.chain().focus().toggleStrike().run(),
    },
    {
      icon: MessageSquareQuote,
      label: 'Quote',
      shortcut: shortcutLabel('B', { shift: true }),
      isActive: selectionState.isBlockquote,
      onAction: () => editor.chain().focus().toggleBlockquote().run(),
    },
    {
      icon: Code,
      label: 'Code',
      shortcut: shortcutLabel('E'),
      isActive: selectionState.isCode,
      onAction: () => editor.chain().focus().toggleCode().run(),
    },
  ];

  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        style={floatingStyles}
        className={cn(
          'animate-selection-toolbar-in z-(--stack-context-menu) flex items-center gap-1 rounded-full p-1',
          onImage
            ? // The pills' glass recipe with a finer 0.5px hairline.
              'bg-black/60 shadow-[inset_0_0_0_0.5px_rgba(255,255,255,0.2),0_4px_24px_0_rgba(0,0,0,0.5)] backdrop-blur-[15px] backdrop-saturate-200'
            : 'border-[0.5px] border-white bg-gray-50 shadow-[0_4px_18px_0_rgba(0,0,0,0.12)] backdrop-blur-md dark:border-white/10 dark:bg-black/60',
        )}
      >
        {buttons.map((button) => (
          <SelectionToolbarButton key={button.label} onImage={onImage} {...button} />
        ))}
      </div>
    </FloatingPortal>
  );
}

export default React.memo(ComposerSelectionToolbar);
